import React, { useState, useEffect, useRef } from 'react';
import { DateRange, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import moment from 'moment';
import { SVGAssets } from '@business/assets/vectors';
import { dateFormat } from '@utils/constants/globals';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';

interface DateRangeState {
  startDate: Date;
  endDate: Date;
  key: string;
}

interface DateRangeFilterProps {
  handleDateRange?: any;
  selectedDateRange?: any;
  singleDateMode?: boolean;
}
const DateRangePicker: React.FC<DateRangeFilterProps> = ({
  handleDateRange,
  selectedDateRange,
  singleDateMode = false
}) => {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [dateRange, setDateRange] = useState<DateRangeState>(() => {
    const dates = selectedDateRange.split(' - ');

    if (singleDateMode) {
      // For single date mode, selectedDateRange contains "startDate - endDate" format
      // where startDate is the selected date and endDate is up to 6 months future (or today)
      const startDate = new Date(dates[0]?.trim()); // The selected start date
      const endDate = new Date(dates[1]?.trim()); // The end date (up to today)
      return {
        startDate: startDate,
        endDate: endDate,
        key: 'selection'
      };
    } else {
      // Normal date range mode - startDate - endDate format
      return {
        startDate: new Date(dates[0]?.trim()),
        endDate: new Date(dates[1]?.trim()),
        key: 'selection'
      };
    }
  });

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  const handleSelect = (ranges: RangeKeyDict) => {
    const { selection } = ranges;
    let startDate, endDate, dateRangeString;

    if (singleDateMode) {
      const selectedDate = moment(selection.startDate || new Date());
      const sixMonthsLater = selectedDate.clone().add(6, 'months');
      const today = moment();

      startDate = selectedDate.format(dateFormat);
      endDate = sixMonthsLater.isAfter(today)
        ? today.format(dateFormat)
        : sixMonthsLater.format(dateFormat);

      dateRangeString = `${startDate} - ${endDate}`;

      handleDateRange(dateRangeString);
      setShowPicker(false); // Close picker after selection in single date mode
    } else {
      // For date range mode, just update the internal state
      // API call will be triggered only when user clicks Apply button
      startDate = moment(selection.startDate || new Date()).format(dateFormat);
      endDate = moment(selection.endDate || new Date()).format(dateFormat);
      dateRangeString = `${startDate} - ${endDate}`;

      // Don't trigger API call here - let the Apply button handle it
    }

    // Calculate end date for single date mode or use selection end date
    let calculatedEndDate;
    if (singleDateMode) {
      const sixMonthsFuture = moment(selection.startDate || new Date()).add(
        6,
        'months'
      );
      calculatedEndDate = sixMonthsFuture.isAfter(moment())
        ? moment().toDate() // Use today if 6 months future exceeds today
        : sixMonthsFuture.toDate(); // Use 6 months future
    } else {
      calculatedEndDate = selection.endDate || new Date();
    }

    setDateRange({
      startDate: selection.startDate || new Date(),
      endDate: calculatedEndDate,
      key: selection.key
    });
  };

  return (
    <div
      ref={datePickerRef}
      className="relative"
      id="date-range-picker"
      data-testid="date-range-picker">
      <Button
        btnStyle="link"
        onClick={() => setShowPicker(!showPicker)}
        data-testid="date-range-picker-btn"
        className="min-h-6 h-10 text-[var(--na-secondary-color-hover)]  border border-[var(--na-primary-color)] w-full text-sm font-medium p-2 mb-4 focus:outline-none px-4 py-2 rounded-lg shadow-md cursor-pointer flex justify-between items-center">
        <span data-testid="date-range-text">
          {singleDateMode
            ? moment(dateRange.startDate).format(dateFormat)
            : `${moment(dateRange.startDate).format(dateFormat)} - ${moment(dateRange.endDate).format(dateFormat)}`}
        </span>
        <SVGAssets.CalenderDownIcon data-testid="calendar-icon" />
      </Button>
      {showPicker && (
        <div
          className={`absolute z-10 mt-0 bg-white shadow-lg rounded-lg ${singleDateMode ? 'right-0' : ''}`}
          data-testid="date-picker">
          <DateRange
            data-testid="date-range-picker"
            ranges={[dateRange]}
            onChange={handleSelect}
            rangeColors={['#FB923C']}
            minDate={moment('2020-04-01').toDate()}
            maxDate={new Date()}
          />
          {!singleDateMode && (
            <div className="p-3 border-t flex gap-2">
              <Button
                onClick={() => setShowPicker(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
                data-testid="cancel-date-range">
                {t('cancel')}
              </Button>
              <Button
                onClick={() => {
                  const startDate = moment(dateRange.startDate).format(
                    dateFormat
                  );
                  const endDate = moment(dateRange.endDate).format(dateFormat);
                  const dateRangeString = `${startDate} - ${endDate}`;
                  handleDateRange(dateRangeString);
                  setShowPicker(false);
                }}
                className="flex-1 bg-[#FB923C] text-white px-4 py-2 rounded hover:bg-[#e8831f] transition-colors"
                data-testid="apply-date-range">
                {t('zipCode.apply')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
